import axios from "axios";
import router from "@/router";
import bus from "@/bus";

import { useAuthStore } from "@/stores/auth";
import { useFeatureFlagsStore } from "@/stores/featureFlags";
import { captureException } from "@/plugins/posthog";

const httpClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

httpClient.interceptors.request.use((config) => {
  const authStore = useAuthStore();
  const token = authStore.getToken();

  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  return config;
});

// Add feature-flags set into headers so that backend will know what's available to the user
httpClient.interceptors.request.use((config) => {
  const featureFlagsStore = useFeatureFlagsStore();
  if (featureFlagsStore.flags) {
    config.headers["feature-flags"] = JSON.stringify(featureFlagsStore.flags);
  }
  return config;
});

httpClient.interceptors.response.use(
  (response) => {
    switch (response.status) {
      case 200:
        if (!response.data.success || response.data.error?.length > 0) {
          return Promise.reject({
            type: "ACTION_FAILED",
            message: response.data.data?.error,
            data: response.data,
            response: response,
          });
        }
        break;
      default:
        // Success without body
        break;
    }

    return response.data.data;
  },
  (error) => {
    captureException(error, {
      url: error.response?.request?.responseURL,
      status: error.response?.status,
      statusText: error.response?.statusText,
      code: error.code,
      message: error.message,
      responseMessage: error.response?.data?.message,
    });
    // Check for actual network errors using Axios error codes
    if (
      !error.response &&
      (error.code === "NETWORK_ERROR" ||
        error.code === "ERR_NETWORK" ||
        error.code === "ERR_INTERNET_DISCONNECTED")
    ) {
      bus.global.emit("notification.top:show", {
        type: "error",
        message:
          "Looks like you lost connection! Please check your internet ⚡",
      });

      return Promise.reject({
        type: "INTERNET_CONNECTION",
        data: error,
      });
    }

    const authStore = useAuthStore();
    switch (error.response.status) {
      case 401:
        // Sorry, you have no access to this action! Try again later, or contact support!

        if (!router.currentRoute.value.path.includes("auth")) {
          bus.global.emit("notification.top:show", {
            type: "error",
            message: "Your session expired. Please login again.",
          });
          authStore.logout();
          location.assign("/auth");
        }

        return Promise.reject({
          type: "ACCESS_ERROR",
          message: error.message,
          data: error,
        });

      case 403:
        return Promise.reject({
          type: "FORBIDDEN",
          data: error,
          message: error.message,
        });

      case 422:
        return Promise.reject({
          type: "FIELDS_VALIDATION_ERROR",
          fields: error.response.data.errors ?? error.response.data.error,
        });

      default:
        // Something went wrong. Contact support, please.
        return Promise.reject({
          type: "SERVER_ERROR",
          data: error,
        });
    }
  }
);

export default httpClient;
